import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowUp, Phone, MessageSquare, Clock, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_META = {
  high:   { label: 'High',   cls: 'text-red-300 bg-red-500/10 border-red-500/40' },
  normal: { label: 'Normal', cls: 'text-purple-300 bg-purple-500/10 border-purple-500/40' },
  low:    { label: 'Low',    cls: 'text-[#A3A3A3] bg-[#262626] border-[#3f3f3f]' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
  catch { return iso; }
};
const fmtTime = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const p = h >= 12 ? 'p' : 'a';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')}${p}`;
};
const daysWaiting = (iso) => {
  if (!iso) return null;
  try { return Math.max(0, Math.floor((Date.now() - new Date(iso)) / 86400000)); } catch { return null; }
};

/**
 * MoveUpListSheet — iter124 Wave M
 * Dispatch-facing sheet that lists every appointment flagged "wants earlier opening".
 * Each row has a "Move to Earlier Slot" inline form that POSTs the move and optionally
 * triggers the reschedule SMS via the parent's existing NotifyCustomerDialog.
 *
 * Props:
 *   open: bool
 *   onOpenChange: (bool) => void
 *   employees: [{id, name}] — tech list for the optional reassign dropdown
 *   onMoved: () => void — parent should refetch the schedule after a successful move
 *   askNotify: (appointmentMeta) => Promise<bool|null> — parent supplies the notify dialog
 *     (returns true=send SMS, false=skip, null=cancel)
 */
const MoveUpListSheet = ({ open, onOpenChange, employees = [], onMoved, askNotify }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [movingId, setMovingId] = useState(null);   // appointment id currently expanded for the move form
  const [moveForm, setMoveForm] = useState({
    new_date: '',
    new_start_time: '',
    new_end_time: '',
    new_employee_id: '',
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/appointments/move-up-list`, { withCredentials: true });
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Move-Up list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const startMove = (appt) => {
    setMovingId(appt.id);
    setMoveForm({
      new_date: '',
      new_start_time: appt.start_time || '',
      new_end_time: appt.end_time || '',
      new_employee_id: appt.employee_id || '',
    });
  };
  const cancelMove = () => { setMovingId(null); setMoveForm({ new_date: '', new_start_time: '', new_end_time: '', new_employee_id: '' }); };

  const submitMove = async (appt) => {
    if (!moveForm.new_date || !moveForm.new_start_time || !moveForm.new_end_time) {
      toast.error('Pick a new date and time window first');
      return;
    }
    if (moveForm.new_date > appt.date || (moveForm.new_date === appt.date && moveForm.new_start_time >= appt.start_time)) {
      toast.error('New slot must be earlier than the current one');
      return;
    }
    // Ask the parent's notify dialog for the customer-comm decision.
    let notify = false;
    if (typeof askNotify === 'function') {
      const res = await askNotify({
        mode: 'move_up',
        customer_name: appt.customer_name,
        from: `${fmtDate(appt.date)} ${fmtTime(appt.start_time)}–${fmtTime(appt.end_time)}`,
        to: `${fmtDate(moveForm.new_date)} ${fmtTime(moveForm.new_start_time)}–${fmtTime(moveForm.new_end_time)}`,
      });
      if (res === null || res === undefined) return;     // cancelled
      notify = !!res;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/appointments/${appt.id}/move-up`,
        {
          new_date: moveForm.new_date,
          new_start_time: moveForm.new_start_time,
          new_end_time: moveForm.new_end_time,
          new_employee_id: moveForm.new_employee_id || null,
          notify_customer: notify,
          notify_type: 'reschedule',
        },
        { withCredentials: true }
      );
      toast.success('Moved to earlier slot');
      cancelMove();
      await load();
      if (typeof onMoved === 'function') onMoved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to move shift');
    } finally {
      setBusy(false);
    }
  };

  const grouped = useMemo(() => {
    const high = items.filter((x) => x.move_up_priority === 'high');
    const norm = items.filter((x) => x.move_up_priority === 'normal');
    const low  = items.filter((x) => x.move_up_priority === 'low');
    return { high, norm, low };
  }, [items]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-xl overflow-y-auto"
        data-testid="moveup-sheet"
      >
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <ArrowUp className="w-4 h-4 text-purple-300" /> Move-Up List
          </SheetTitle>
          <SheetDescription className="text-[#A3A3A3]">
            Customers who want earlier service if an opening appears. The job stays on its real scheduled date until you move it.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {loading && <p className="text-[#A3A3A3] text-sm">Loading…</p>}
          {!loading && items.length === 0 && (
            <div data-testid="moveup-empty" className="rounded-md border border-[#262626] bg-[#0A0A0A] p-6 text-center">
              <p className="text-white font-medium">No customers waiting for an earlier opening.</p>
              <p className="text-[#A3A3A3] text-xs mt-1">Toggle &quot;Wants earlier opening&quot; inside a shift to add one.</p>
            </div>
          )}

          {[
            { key: 'high', label: 'High Priority',  rows: grouped.high },
            { key: 'norm', label: 'Normal',          rows: grouped.norm },
            { key: 'low',  label: 'Low Priority',    rows: grouped.low },
          ].map((bucket) => bucket.rows.length > 0 && (
            <div key={bucket.key} className="space-y-2" data-testid={`moveup-bucket-${bucket.key}`}>
              <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold">{bucket.label} ({bucket.rows.length})</p>
              {bucket.rows.map((appt) => {
                const meta = PRIORITY_META[appt.move_up_priority] || PRIORITY_META.normal;
                const wait = daysWaiting(appt.move_up_requested_at);
                const isOpen = movingId === appt.id;
                return (
                  <div key={appt.id} data-testid={`moveup-row-${appt.id}`} className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-medium truncate">{appt.customer_name || '—'}</p>
                          <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-bold ${meta.cls}`}>
                            {meta.label}
                          </span>
                          {wait !== null && (
                            <span className="text-[10px] text-[#A3A3A3]">
                              waiting {wait}d
                            </span>
                          )}
                        </div>
                        <p className="text-[#D4AF37] text-xs font-medium mt-0.5">{appt.shift_title || appt.service_type || 'Appointment'}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Current</p>
                        <p className="text-white text-sm font-medium">{fmtDate(appt.date)}</p>
                        <p className="text-[#A3A3A3] text-xs">{fmtTime(appt.start_time)} – {fmtTime(appt.end_time)}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#A3A3A3]">
                      {appt.employee_name && <span>Tech: <span className="text-white">{appt.employee_name}</span></span>}
                      {appt.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {appt.address}</span>}
                      {appt.move_up_preferred_window && (
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Prefers: {appt.move_up_preferred_window}</span>
                      )}
                    </div>
                    {appt.move_up_notes && (
                      <p className="text-[#E5E5E5] text-xs italic bg-[#141414] border-l-2 border-purple-500/40 pl-2 py-1">
                        “{appt.move_up_notes}”
                      </p>
                    )}

                    <div className="flex items-center gap-2 flex-wrap">
                      {appt.customer_phone && (
                        <a
                          data-testid={`moveup-call-${appt.id}`}
                          href={`tel:${appt.customer_phone}`}
                          className="inline-flex items-center gap-1 text-[11px] text-[#D4AF37] hover:underline"
                        >
                          <Phone className="w-3 h-3" /> Call
                        </a>
                      )}
                      {appt.customer_phone && (
                        <a
                          data-testid={`moveup-sms-${appt.id}`}
                          href={`sms:${appt.customer_phone}`}
                          className="inline-flex items-center gap-1 text-[11px] text-[#D4AF37] hover:underline"
                        >
                          <MessageSquare className="w-3 h-3" /> Text
                        </a>
                      )}
                      <div className="ml-auto flex items-center gap-2">
                        {!isOpen ? (
                          <Button
                            data-testid={`moveup-start-${appt.id}`}
                            size="sm"
                            onClick={() => startMove(appt)}
                            className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-8 font-bold"
                          >
                            <ArrowUp className="w-3.5 h-3.5 mr-1" /> Move to Earlier Slot
                          </Button>
                        ) : (
                          <Button
                            data-testid={`moveup-cancel-${appt.id}`}
                            size="sm"
                            variant="outline"
                            onClick={cancelMove}
                            className="border-[#262626] text-white h-8"
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </div>

                    {isOpen && (
                      <div className="mt-2 grid grid-cols-2 gap-2 rounded-md border border-purple-500/30 bg-purple-500/[0.04] p-3" data-testid={`moveup-form-${appt.id}`}>
                        <div className="space-y-1">
                          <Label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">New Date</Label>
                          <Input
                            data-testid={`moveup-form-date-${appt.id}`}
                            type="date"
                            max={appt.date}
                            value={moveForm.new_date}
                            onChange={(e) => setMoveForm({ ...moveForm, new_date: e.target.value })}
                            className="bg-[#0A0A0A] border-[#262626] text-white h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">Reassign Tech (optional)</Label>
                          <Select
                            value={moveForm.new_employee_id || 'keep'}
                            onValueChange={(v) => setMoveForm({ ...moveForm, new_employee_id: v === 'keep' ? (appt.employee_id || '') : v })}
                          >
                            <SelectTrigger data-testid={`moveup-form-tech-${appt.id}`} className="bg-[#0A0A0A] border-[#262626] text-white h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#141414] border-[#262626]">
                              <SelectItem value="keep" className="text-white">Keep current tech</SelectItem>
                              {employees.map((e) => (
                                <SelectItem key={e.id} value={e.id} className="text-white">{e.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">New Start</Label>
                          <Input
                            data-testid={`moveup-form-start-${appt.id}`}
                            type="time"
                            value={moveForm.new_start_time}
                            onChange={(e) => setMoveForm({ ...moveForm, new_start_time: e.target.value })}
                            className="bg-[#0A0A0A] border-[#262626] text-white h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">New End</Label>
                          <Input
                            data-testid={`moveup-form-end-${appt.id}`}
                            type="time"
                            value={moveForm.new_end_time}
                            onChange={(e) => setMoveForm({ ...moveForm, new_end_time: e.target.value })}
                            className="bg-[#0A0A0A] border-[#262626] text-white h-9"
                          />
                        </div>
                        <div className="col-span-2 flex justify-end">
                          <Button
                            data-testid={`moveup-confirm-${appt.id}`}
                            disabled={busy}
                            onClick={() => submitMove(appt)}
                            className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold h-9"
                          >
                            {busy ? 'Moving…' : 'Confirm Move'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MoveUpListSheet;
